import { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";

import useEmployeeDocumentPersonal from "../../../hooks/Employee/useEmployeeDocumentPersonal";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Edit,
  FileText,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import useAuthAdmin from "../../../hooks/Admin/useAuthAdmin";
import { handleApiError } from "../../../utils/handleApiError";
const documentItems = [
  {
    id: "1",
    title: "Credit Check, PEP & Sanctions",
    status: "uploaded",
    type: "creditcheck",
  },
  {
    id: "2",
    title: "Identity Document 2",
    status: "pending",
    type: "identity",
  },
  {
    id: "3",
    title: "Driving Licence",
    status: "uploaded",
    type: "drivinglicence",
  },
  {
    id: "4",
    title: "Identity Document 2 Rear",
    status: "pending",
    type: "identityrear",
  },
  {
    id: "5",
    title: "Driving Licence (Rear)",
    status: "uploaded",
    type: "drivinglicencerear",
  },
  { id: "6", title: "Proof of Address 1", status: "uploaded", type: "address" },
  { id: "7", title: "Passport/VISA", status: "pending", type: "passport" },
  {
    id: "8",
    title: "Proof of Address 2",
    status: "uploaded",
    type: "addressrear",
  },
  {
    id: "9",
    title: "Passport/VISA Rear",
    status: "pending",
    type: "passportrear",
  },
  {
    id: "10",
    title: "SIA Licence Front",
    status: "uploaded",
    type: "sialicence",
  },
  {
    id: "11",
    title: "Identity Document 1",
    status: "uploaded",
    type: "identityfront",
  },
  {
    id: "12",
    title: "SIA Licence Rear",
    status: "uploaded",
    type: "sialicencerear",
  },
  {
    id: "13",
    title: "Identity Document 1 Rear",
    status: "uploaded",
    type: "identityfrontrear",
  },
  {
    id: "14",
    title: "SIA Initial Validation Check",
    status: "uploaded",
    type: "initialvalidation",
  },
];

// --- New Preview Modal Component ---
const DocumentPreviewModal = ({
  isOpen,
  onClose,
  doc,
  onDownload,
  onEdit,
  onDelete,
}) => {
  if (!isOpen || !doc) return null;

  return (
    <>
      <Modal
        show={isOpen}
        onHide={onClose}
        size="lg"
        centered
        className="w-50-modal"
      >
        {/* ${("DocumentPreviewModal rendered with doc:", doc.url)} */}
        <Modal.Body>
          <div className="">
            <img
              src={`${doc.url}?download=2`}
              alt={`Preview of ${doc.title}`}
              className="modal-image"
              style={{ width: "300px", height: "300px" }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-right">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <a
            href={`${doc.url}?download=1`}
            download={doc.title}
            className="btn-action btn-download"
          >
            <Download size={18} /> Download
          </a>
          <button
            className="btn-action btn-edit"
            onClick={() => onEdit(doc.type)}
          >
            <Edit size={18} /> Edit
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default function EmployeeDocumentView() {
  const { documents, refetch } = useEmployeeDocumentPersonal();
  const navigate = useNavigate();
  const { id } = useParams();

  // --- State for Modal ---
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const fileInputRef = useRef(null);

  // --- Handlers for Modal and Actions ---
  const handlePreview = (doc) => {
    setSelectedDoc(doc);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setSelectedDoc(null);
  };

  const handleFileSelectForEdit = async (e, docType) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      console.log(`Re-uploading for ${docType}:`, file);
      console.log(`Re-uploading for`, selectedDoc);

      // Run submit immediately for this selected file
      await handleSubmit(file);
    }
  };

  const handleEdit = (docType, e) => {
    if (e) e.stopPropagation();
    fileInputRef.current.dataset.docType = docType; // Store type for the change handler
    fileInputRef.current.click();
  };

  const handleDelete = async (docId, e) => {
    if (e) e.stopPropagation(); // Prevent card click

    if (window.confirm("Are you sure you want to delete this document?")) {
      console.log("Deleting document:", docId);
      // await deleteDocument(docId); // Example call to your hook
      handleClosePreview(); // Close modal if action was from modal
    }
  };

  const { adminSession, logout } = useAuthAdmin();

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${adminSession}`,
    }),
    [adminSession]
  );

  const handleSubmit = async (fileToUpload) => {
    // If either file or document is missing, skip.
    if (!fileToUpload || !selectedDoc) {
      return;
    }

    try {
      // 1. DELETE existing document
      const deleteRes = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/employees/documents/${selectedDoc.id}`,
        { headers }
      );
      console.log("Delete response:", deleteRes.data);

      if (deleteRes.status === 200) {
        // 2. CREATE new document
        const formData = new FormData();
        formData.append("documents", fileToUpload); // key must match backend
        // no need to append 'type' if it's part of the URL

        const createRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/employees/documents/${id}/${selectedDoc.type}`,
          formData,
          {
            headers: {
              ...headers,
            },
          }
        );

        if (createRes.status === 200) {
          refetch();
          handleClosePreview();
          setSelectedFile(null);
          setSelectedDoc(null);
          alert("Photo updated successfully.");
        } else {
          throw new Error("Create failed");
        }
      } else {
        throw new Error("Delete failed");
      }
    } catch (error) {
      handleApiError(error, logout, navigate);
      alert("Failed to update document.");
    } finally {
      // Ensure we don't accidentally re-trigger uploads
      setSelectedFile(null);
    }
  };

  // --- Utility for downloading file ---
  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      // Extract file extension from URL or use a default
      const extension = url.split(".").pop() || "jpg";
      link.download = `${filename.replace(/\s+/g, "_")}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Could not download the file.");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "uploaded":
        return <CheckCircle size={16} className="text-success" />;
      case "pending":
        return <Clock size={16} className="text-warning" />;
      case "required":
        return <AlertCircle size={16} className="text-danger" />;
      default:
        return <CheckCircle size={16} className="text-success" />;
    }
  };

  const BASE_URL = "https://docs.guardsoft.co.uk/"; // Prefix for document URLs

  return (
    <>
      <div className="aside-collapsed">
        {/* <div className="wrapper"> */}
        <section>
          <div
            className="content-wrapper"
            style={{ backgroundColor: "#ecf0f5" }}
          >
            {/* Hidden File Input for Edit/Re-upload */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) =>
                handleFileSelectForEdit(e, fileInputRef.current.dataset.docType)
              }
              accept="image/*,.pdf"
            />
            <div class="row">
              <div className="col-md-12">
                <div className="panel panel-default">
                  <div className="document-upload-container">
                    <div className="container-fluid px-4">
                      {/* Header */}
                      <div className="mb-4">
                        <h2 className="document-header">Documents</h2>
                        {/* <p className="document-status">Uploaded</p> */}
                      </div>
                      <div className="document-grid">
                        {documentItems?.map((item) => {
                          // --- Simplified Logic per item ---
                          const typeDocs = documents[item.type];
                          const docExists = typeDocs && typeDocs.length > 0;
                          const latestDoc = docExists
                            ? typeDocs[typeDocs.length - 1]
                            : null;
                          const docUrl = latestDoc
                            ? BASE_URL + latestDoc.documentUrl
                            : null;
                          const status = docExists ? "uploaded" : "pending";

                          const fullDocPayload = latestDoc
                            ? {
                                id: latestDoc.id, // Assuming your doc object has an id
                                url: docUrl,
                                title: item.title,
                                type: item.type,
                              }
                            : null;

                          return (
                            <div
                              key={item.id}
                              className="document-card-wrapper"
                            >
                              <div
                                className={`document-card ${
                                  !docExists ? "disabled" : ""
                                } `}
                                onClick={() =>
                                  docExists && handlePreview(fullDocPayload)
                                }
                                style={{
                                  border: docExists
                                    ? "1px solid #ccc"
                                    : "1px solid red",
                                  background: docExists
                                    ? "white"
                                    : "rgb(251 231 232)",
                                }}
                              >
                                <div className="document-icon-container">
                                  <FileText
                                    className="document-icon"
                                    size={24}
                                  />
                                </div>
                                <div className="document-content">
                                  <span
                                    className={`document-title `}
                                    style={{
                                      color:
                                        status === "uploaded"
                                          ? "#0083ff"
                                          : "#f22e2e",
                                    }}
                                  >
                                    {item.title}
                                  </span>
                                  <div className="document-status-indicator d-flex gap-2 align-items-center">
                                    {getStatusIcon(status)}
                                    <span className="status-text">
                                      {status.charAt(0).toUpperCase() +
                                        status.slice(1)}
                                    </span>
                                  </div>
                                </div>
                                {/* --- Action Icons on Card --- */}
                                {/* {docExists && (
                                                  <div className="card-actions">
                                                    <button onClick={(e) => handleEdit(item.type, e)} className="action-icon-btn">
                                                      <Edit size={18} />
                                                    </button>
                                                    <button onClick={(e) => handleDelete(latestDoc.id, e)} className="action-icon-btn action-icon-delete">
                                                      <Trash2 size={18} />
                                                    </button>
                                                  </div>
                                                )} */}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <style jsx>{`
                      /* --- Existing Styles --- */
                      .document-grid {
                        display: grid;
                        grid-template-columns: 1fr;
                        gap: 16px;
                      }
                      @media (min-width: 768px) {
                        .document-grid {
                          grid-template-columns: repeat(2, 1fr);
                        }
                      }
                      @media (min-width: 1024px) {
                        .document-grid {
                          grid-template-columns: repeat(3, 1fr);
                        }
                      }
                      .document-card-wrapper {
                        width: 100%;
                      }
                      .document-upload-container {
                        background-color: #f8f9fa;
                        min-height: 80vh;
                        font-family: -apple-system, BlinkMacSystemFont,
                          "Segoe UI", Roboto, sans-serif;
                        padding-top: 1rem;
                      }
                      .document-header {
                        font-size: 2.5rem;
                        font-weight: 600;
                        color: #333;
                        margin-bottom: 0.25rem;
                      }
                      .document-status {
                        color: #6c757d;
                        font-size: 0.9rem;
                        margin-bottom: 0;
                      }
                      .document-card {
                        background: white;
                        border-radius: 4px;
                        // padding: 4px;
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                        border: 1px solid #e9ecef;
                        transition: all 0.2s ease;
                        // height: 80px;
                        position: relative;
                        overflow: hidden;
                      }
                      .document-card:not(.disabled) {
                        cursor: pointer;
                      }
                      .document-card:not(.disabled):hover {
                        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                        transform: translateY(-2px);
                      }
                      .document-card.disabled {
                        background-color: #f1f3f5;
                        cursor: not-allowed;
                      }
                      .document-icon-container {
                        background: #0083ff;
                        // border-radius: 4px;
                        padding: 0.75rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                        width: 48px;
                        height: 48px;
                      }
                      .document-icon {
                        color: white;
                      }
                      .document-content {
                        flex: 1;
                        min-width: 0;
                      }
                      .document-title {
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 1.3;
                        display: block;
                        word-wrap: break-word;
                      }
                      .status-text {
                        text-transform: capitalize;
                      }
                      .text-primary {
                        color: #007bff !important;
                      }
                      .text-danger {
                        color: #6c757d !important;
                      } /* Changed pending to grey */
                      .text-success {
                        color: #28a745 !important;
                      }
                      .text-warning {
                        color: #ffc107 !important;
                      }

                      /* --- New Styles for Actions and Modal --- */
                      .card-actions {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        position: absolute;
                        right: 1rem;
                        top: 50%;
                        transform: translateY(-50%);
                      }
                      .action-icon-btn {
                        background: #e9ecef;
                        border: none;
                        border-radius: 50%;
                        width: 32px;
                        height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        color: #495057;
                        transition: all 0.2s ease;
                      }
                      .action-icon-btn:hover {
                        background: #ced4da;
                        color: #212529;
                      }
                      .action-icon-delete:hover {
                        background: #f8d7da;
                        color: #721c24;
                      }
                      .modal-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(0, 0, 0, 0.7);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 1000;
                      }
                      .modal-content {
                        background: white;
                        padding: 1.5rem;
                        border-radius: 12px;
                        max-width: 90vw;
                        max-height: 90vh;
                        display: flex;
                        flex-direction: column;
                        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
                        width: 800px;
                      }
                      .modal-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        border-bottom: 1px solid #e9ecef;
                        padding-bottom: 1rem;
                        margin-bottom: 1rem;
                      }
                      .modal-title {
                        font-size: 1.5rem;
                        margin: 0;
                      }
                      .modal-close-btn {
                        background: none;
                        border: none;
                        cursor: pointer;
                        padding: 0.5rem;
                        line-height: 1;
                      }
                      .modal-body {
                        flex-grow: 1;
                        overflow-y: auto;
                        text-align: center;
                      }
                      .modal-image {
                        max-width: 100%;
                        max-height: 70vh;
                        object-fit: contain;
                      }
                      .modal-footer {
                        display: flex;
                        justify-content: flex-end;
                        gap: 1rem;
                        padding-top: 1.5rem;
                        border-top: 1px solid #e9ecef;
                        margin-top: 1rem;
                      }
                      .btn-action {
                        padding: 0.6rem 1.2rem;
                        border-radius: 8px;
                        border: none;
                        font-size: 1rem;
                        font-weight: 500;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        transition: background-color 0.2s ease;
                      }
                      .btn-download {
                        background-color: #007bff;
                        color: white;
                      }
                      .btn-download:hover {
                        background-color: #0056b3;
                      }
                      .btn-edit {
                        background-color: #ffc107;
                        color: #212529;
                      }
                      .btn-edit:hover {
                        background-color: #e0a800;
                      }
                      .btn-delete {
                        background-color: #dc3545;
                        color: white;
                      }
                      .btn-delete:hover {
                        background-color: #c82333;
                      }
                    `}</style>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* --- Render Modal Component --- */}
        <DocumentPreviewModal
          isOpen={isPreviewOpen}
          onClose={handleClosePreview}
          doc={selectedDoc}
          onDownload={handleDownload}
          onEdit={(docType) => handleEdit(docType)}
          onDelete={(docId) => handleDelete(docId)}
        />
        <footer>
          <div className="row" style={{ margin: 0 }}>
            <div className="col-md-12">
              <a href="##" data-toggle="modal" data-target="#termsOfUse">
                Terms of use of ShiftSecure
              </a>
            </div>
          </div>
        </footer>
        {/* </div> */}
      </div>
    </>
  );
}
